import {
	ContractSuretyType,
	isCommercialSuretyBondType,
	SuretyAccountId,
	type SuretyQuoteId,
} from "@inrev/common";
import { useFetchBondFormTemplates } from "@portal/api/bond-request";
import { useFileClassificationPipeline } from "@portal/api/bond-request/agent";
import {
	getAllowedBondFormUploadTypes,
	getFilteredBondFormTemplates,
} from "@portal/views/shared/bond/bondUtils";
import { type FC, useEffect, useMemo, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { PROJECT_NAME_TOOLTIP } from "../../../../constants";
import { FormInput } from "../../../form/fields/FormInput";
import { FormMoney } from "../../../form/fields/FormMoney";
import { FormTextArea } from "../../../form/fields/FormTextArea";
import { Button } from "../../../primitives/Button";
import { Tooltip } from "../../../ui/Tooltip";
import { useBondRequestSubmissionAdapter } from "../BondRequestSubmissionAdapterContext";
import { resolveContractSuretyType } from "../bondRequestSubmissionBondTypeUtils";
import {
	B2F_FILES_STEP_FIELDS,
	FILES_STEP_FIELDS,
	type SubmissionFormData,
} from "../bondRequestSubmissionFormSchema";
import { BidToFinalFields } from "./BidToFinalFields";
import { buildCreateQuotePayload } from "./buildCreateQuotePayload";
import { type DocumentFile } from "./DocumentFileRow";
import { DocumentUploadSection } from "./DocumentUploadSection";
import { StepFieldLabel } from "./StepFieldLabel";

export type BondRequestSubmitErrorName = "SubmissionRequestFailed" | "InvalidSubmissionState";

export type FilesStepProps = {
	accountId: SuretyAccountId;
	isSubmitting?: boolean;
	onBack: () => void;
	onSubmitting: () => void;
	onSubmitted: (quoteId: SuretyQuoteId) => void;
	onSubmitError: (errorName: BondRequestSubmitErrorName) => void;
};

export const FilesStep: FC<FilesStepProps> = ({
	accountId,
	isSubmitting,
	onBack,
	onSubmitting,
	onSubmitted,
	onSubmitError,
}) => {
	const { submitBondRequest, companies, companiesIsLoading } = useBondRequestSubmissionAdapter();
	const formMethods = useFormContext<SubmissionFormData>();
	const { control, getValues, setValue, trigger } = formMethods;

	const bondType = useWatch({ control, name: "bondType" });
	const contractBondType = useWatch({ control, name: "contractBondType" });
	const { bondFormTemplates } = useFetchBondFormTemplates();

	const contractSuretyType = resolveContractSuretyType(bondType, contractBondType);

	const filteredBondFormTemplates = useMemo(
		() => getFilteredBondFormTemplates(bondFormTemplates, contractSuretyType),
		[bondFormTemplates, contractSuretyType],
	);

	const allowedBondFormUploadTypes = useMemo(
		() => getAllowedBondFormUploadTypes(contractSuretyType),
		[contractSuretyType],
	);

	// The modal's focus trap redirects focus to the first focusable element whenever the
	// previously-focused element (the prior step's "Next" button) unmounts. Claiming that
	// redirect ourselves, on an inert container, keeps it from landing in the Project Name input.
	const stepContainerRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		stepContainerRef.current?.focus();
	}, []);

	const { files, handleDrop, handleDelete, isAnyUploading, subscribers } =
		useFileClassificationPipeline({});

	const onBondAmountBlur = () => {
		if (getValues("contractAmountEdited")) return;
		const bondAmount = getValues("bondAmount");
		if (bondAmount) {
			setValue("contractAmount", bondAmount);
		}
	};
	const onContractAmountChange = () => {
		setValue("contractAmountEdited", true);
	};

	const handleSubmit = async () => {
		const fields =
			contractSuretyType === ContractSuretyType.bid_to_final
				? B2F_FILES_STEP_FIELDS
				: FILES_STEP_FIELDS;
		if (!(await trigger([...fields]))) return;
		const values = getValues();
		if (!values.principal || !values.bondType) return;
		if (isCommercialSuretyBondType(values.bondType)) {
			onSubmitError("InvalidSubmissionState");
			return;
		}
		const contractSuretyTypeForSubmit = resolveContractSuretyType(
			values.bondType,
			values.contractBondType,
		);
		if (!contractSuretyTypeForSubmit) {
			onSubmitError("InvalidSubmissionState");
			return;
		}
		const principalName = (companies ?? []).find((c) => c.id === values.principal)?.name ?? "";
		if (!principalName) {
			onSubmitError("InvalidSubmissionState");
			return;
		}
		const uploadedFiles = files.flatMap((f) =>
			f.uploadedFile ? [{ ...f.uploadedFile, types: f.types }] : [],
		);
		const payload = buildCreateQuotePayload({
			contractSuretyType: contractSuretyTypeForSubmit,
			principal: values.principal,
			principalName,
			confirmedValues: values,
			uploadedFiles,
			comment: values.submissionNarrative,
			bondForm: values.bondForm,
		});
		onSubmitting();
		submitBondRequest(payload, {
			accountId: accountId,
			agentId: values.agentId,
			onSuccess: (quoteId) => onSubmitted(quoteId),
			onError: () => onSubmitError("SubmissionRequestFailed"),
		});
	};

	const classifiedTypes = useMemo(() => new Set(files.flatMap((f) => f.types)), [files]);
	const documentFiles = useMemo<DocumentFile[]>(
		() =>
			files.map((f) => ({
				id: f.fileId,
				name: f.name,
				types: f.types,
				status: f.classification.status,
				classificationStatus: f.uploadedFile?.classificationStatus,
			})),
		[files],
	);

	return (
		<div
			ref={stepContainerRef}
			tabIndex={-1}
			className="flex flex-col gap-5 px-6 pt-5 pb-8 outline-none"
		>
			{subscribers}

			{contractSuretyType === ContractSuretyType.bid_to_final && (
				<BidToFinalFields
					formMethods={formMethods}
					minimal
					showRequiredMarkers
					onBondAmountBlur={onBondAmountBlur}
					onContractAmountChange={onContractAmountChange}
					names={{
						actualBidAmount: "actualBidAmount",
						secondBid: "secondBid",
						bondAmount: "bondAmount",
						contractAmount: "contractAmount",
						projectName: "projectName",
					}}
				/>
			)}

			{contractSuretyType !== ContractSuretyType.bid_to_final && (
				<div className="flex flex-col gap-3">
					<div className="flex items-center gap-1.5">
						<StepFieldLabel required>Project Name</StepFieldLabel>
						<Tooltip
							id="project-name-guidance"
							content={PROJECT_NAME_TOOLTIP}
							ariaLabel="Project Name guidance"
							className="text-[16px]"
						/>
					</div>
					<FormInput<SubmissionFormData>
						name="projectName"
						control={control}
						testId="project-name-input"
						placeholder="Enter project name"
					/>
				</div>
			)}

			{contractSuretyType === ContractSuretyType.bid && (
				<div className="flex flex-col gap-3">
					<StepFieldLabel required>Bid Amount</StepFieldLabel>
					<FormMoney<SubmissionFormData>
						name="bidAmount"
						control={control}
						testId="bid-amount-input"
						placeholder=""
					/>
				</div>
			)}

			<DocumentUploadSection
				classifiedTypes={classifiedTypes}
				files={documentFiles}
				onDrop={handleDrop}
				onRemoveFile={handleDelete}
				uploadDisabled={isSubmitting || !contractSuretyType}
				contractSuretyType={contractSuretyType}
				trackingPrefix="bond-request-modal"
				bondForm={{
					control,
					templates: filteredBondFormTemplates,
					allowedUploadTypes: allowedBondFormUploadTypes,
				}}
			/>

			<div className="flex flex-col gap-1.5">
				<StepFieldLabel>Submission Narrative</StepFieldLabel>
				<FormTextArea<SubmissionFormData>
					name="submissionNarrative"
					control={control}
					className="h-[110px]"
					testId="submission-narrative-input"
					placeholder="Tell us about the project, important amounts and dates, special requirements, relevant experience, and anything else that may help us understand this risk."
				/>
			</div>

			<div className="flex justify-end gap-2 pt-1">
				<Button color="secondary" onClick={onBack} disabled={isSubmitting}>
					Back
				</Button>
				<Button
					color="primary"
					disabled={isAnyUploading || companiesIsLoading}
					loading={isSubmitting}
					onClick={handleSubmit}
					trackingId="bond-request-modal-files-submit"
				>
					Submit Request
				</Button>
			</div>
		</div>
	);
};
